import {
  BayLevelEnum,
  IBayLevelData,
  ILashingBridge,
  ILashingBridgeInfo,
} from "open-vessel-definition";
import {
  IBayPattern,
  ILashingBridgeSlot,
  IRowPattern,
  LASHING_BRIDGE_SIDES,
  TLashingBridgePerRow,
  TLashingBridgeSide,
  normalizeLashingBridgeInfo,
  sortRowsArray,
} from "@tedivo/tedivo-bay-grid-pure";
import { pad3, sortByMultipleFields } from "@tedivo/tedivo-pure-helpers";

export type TDraftKey = `${IBayPattern}.${TLashingBridgeSide}`;

/** What the editor holds for one side of one bay */
export interface ILashingBridgeDraft {
  on: boolean;
  tiers?: number;
  height?: number;
  lcg?: number;
  perRow: TLashingBridgePerRow;
}

export const draftKey = (isoBay: IBayPattern, side: TLashingBridgeSide): TDraftKey =>
  `${isoBay}.${side}`;

/** Tiers must be a non-negative integer */
export const isValidTiers = (v: number | undefined): v is number =>
  v !== undefined && Number.isInteger(v) && v >= 0;

/** Height is optional; when present it must be a non-negative number */
export const isValidHeight = (v: number | undefined): boolean =>
  v === undefined || (Number.isFinite(v) && v >= 0);

export function draftFromBridge(b: ILashingBridge | undefined): ILashingBridgeDraft {
  if (!b) return { on: false, perRow: {} };

  return {
    on: true,
    tiers: b.tiers,
    height: b.height,
    lcg: b.lcg,
    perRow: clonePerRow(b.perRow),
  };
}

/** `undefined` when the side is off. Undefined fields and an empty `perRow` are omitted */
export function bridgeFromDraft(d: ILashingBridgeDraft): ILashingBridge | undefined {
  if (!d.on) return undefined;

  const bridge: ILashingBridge = { tiers: d.tiers ?? 0 };
  if (d.height !== undefined) bridge.height = d.height;
  if (d.lcg !== undefined) bridge.lcg = d.lcg;
  if (Object.keys(d.perRow).length) bridge.perRow = clonePerRow(d.perRow);

  return bridge;
}

/** Drafts of every side of every odd bay, read from the Above bay-levels */
export function readLashingBridgeDrafts(
  bls: IBayLevelData[],
  isoBays: number,
): Map<TDraftKey, ILashingBridgeDraft> {
  const drafts = new Map<TDraftKey, ILashingBridgeDraft>();

  for (let iBay = 1; iBay <= isoBays; iBay += 2) {
    const isoBay = pad3(iBay) as IBayPattern;
    const info = normalizeLashingBridgeInfo(findAbove(bls, isoBay)?.lashingBridges);

    LASHING_BRIDGE_SIDES.forEach((side) => {
      drafts.set(draftKey(isoBay, side), draftFromBridge(info?.[side]));
    });
  }

  return drafts;
}

/**
 * Writes the drafts to the Above bay-levels (mutates `bls`).
 * Creates the Above bay-level when it's missing and a bridge exists; removes
 * `lashingBridges` when both sides are off.
 * @returns Whether anything changed
 */
export function applyLashingBridgeDrafts(
  bls: IBayLevelData[],
  isoBays: number,
  drafts: Map<TDraftKey, ILashingBridgeDraft>,
): boolean {
  let changed = false;
  let added = false;

  for (let iBay = 1; iBay <= isoBays; iBay += 2) {
    const isoBay = pad3(iBay) as IBayPattern;

    const info: ILashingBridgeInfo = {};
    LASHING_BRIDGE_SIDES.forEach((side) => {
      const draft = drafts.get(draftKey(isoBay, side));
      const bridge = draft ? bridgeFromDraft(draft) : undefined;
      if (bridge) info[side] = bridge;
    });

    const hasBridges = !!(info.fore || info.aft);
    const bl = findAbove(bls, isoBay);

    if (!bl) {
      if (!hasBridges) continue;
      bls.push({ isoBay, level: BayLevelEnum.ABOVE, infoByContLength: {}, lashingBridges: info });
      changed = added = true;
      continue;
    }

    const prev = JSON.stringify(bl.lashingBridges);

    if (hasBridges) bl.lashingBridges = info;
    else delete bl.lashingBridges;

    if (prev !== JSON.stringify(bl.lashingBridges)) changed = true;
  }

  if (added)
    bls.sort(
      sortByMultipleFields([
        { name: "isoBay", ascending: true },
        { name: "level", ascending: true },
      ]),
    );

  return changed;
}

/** A line of the editor: an offered slot, or a bridge stored on a position that isn't one */
export interface ILashingBridgeRow {
  key: TDraftKey;
  isoBay: IBayPattern;
  side: TLashingBridgeSide;
  /** Block of bays label (`"005-007"`, `"009"`) */
  block: string;
  /** False for a bridge stored on a position that isn't offered (shown flagged) */
  expected: boolean;
  /** For a flagged row: the offered slot that describes the same space */
  sharedWith?: TDraftKey;
}

/**
 * Lines of the editor, bow to stern: every slot of `getLashingBridgeSlots`, plus every bridge
 * stored on a position that isn't a slot (flagged, so the user can turn it off).
 * Without slots (no CGs), two lines per bay, as every position is possible.
 */
export function buildLashingBridgeRows(
  slots: ILashingBridgeSlot[] | undefined,
  drafts: Map<TDraftKey, ILashingBridgeDraft>,
  isoBays: number,
): ILashingBridgeRow[] {
  const rows: ILashingBridgeRow[] = [];

  if (!slots) {
    for (let iBay = 1; iBay <= isoBays; iBay += 2) {
      const isoBay = pad3(iBay) as IBayPattern;
      LASHING_BRIDGE_SIDES.forEach((side) =>
        rows.push({ key: draftKey(isoBay, side), isoBay, side, block: isoBay, expected: true }),
      );
    }
  } else {
    slots.forEach((s) =>
      rows.push({
        key: draftKey(s.isoBay, s.side),
        isoBay: s.isoBay,
        side: s.side,
        block: s.block,
        expected: true,
      }),
    );

    const slotKeys = new Set(rows.map((r) => r.key));
    const blockOf = (isoBay: IBayPattern) =>
      slots.find((s) => s.block.split("-").includes(isoBay))?.block ?? isoBay;

    drafts.forEach((draft, key) => {
      if (!draft.on || slotKeys.has(key)) return;
      const [isoBay, side] = key.split(".") as [IBayPattern, TLashingBridgeSide];
      const sharing = slots.find(
        (s) => s.sharedWith && draftKey(s.sharedWith.isoBay, s.sharedWith.side) === key,
      );

      rows.push({
        key,
        isoBay,
        side,
        block: blockOf(isoBay),
        expected: false,
        sharedWith: sharing ? draftKey(sharing.isoBay, sharing.side) : undefined,
      });
    });
  }

  // Bow to stern: FWD of a bay, then its AFT
  const order = (r: ILashingBridgeRow) => Number(r.isoBay) * 2 + (r.side === "aft" ? 1 : 0);
  return rows.sort((a, b) => order(a) - order(b));
}

/**
 * Short text with the per-row exceptions, sorted port to starboard: `"04:0 · 02:1 · 01:2 (3.2)"`.
 * Rows that the bay doesn't have get a `*`.
 */
export function summarizePerRow(
  perRow: TLashingBridgePerRow,
  rowsInBay: IRowPattern[],
  fmtHeight: (mm: number) => string,
): { text: string; count: number; outside: number } {
  const rows = (Object.keys(perRow) as IRowPattern[]).sort(sortRowsArray);
  let outside = 0;

  const parts = rows.map((row) => {
    const h = perRow[row];
    const isOutside = !rowsInBay.includes(row);
    if (isOutside) outside += 1;

    const height = h.height !== undefined ? ` (${fmtHeight(h.height)})` : "";
    return `${row}${isOutside ? "*" : ""}:${h.tiers}${height}`;
  });

  return { text: parts.length ? parts.join(" · ") : "—", count: parts.length, outside };
}

function findAbove(bls: IBayLevelData[], isoBay: IBayPattern): IBayLevelData | undefined {
  return bls.find((bl) => bl.level === BayLevelEnum.ABOVE && bl.isoBay === isoBay);
}

function clonePerRow(perRow: TLashingBridgePerRow | undefined): TLashingBridgePerRow {
  const out: TLashingBridgePerRow = {};
  (Object.keys(perRow || {}) as IRowPattern[]).forEach((row) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    out[row] = { ...perRow![row] };
  });
  return out;
}
